import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { brandIcons } from '../../config/branding';
import { AvailableStation } from '../../interface/station-management.interface';
import { useAppDispatch } from '../../redux/hooks';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import chargingStation from '../../utils/api/station';
import CustomTable from '../custom-table/CustomTable';

interface StationSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
}

interface AvailableTabProperties {
  label: string;
  key: string;
  isActive: boolean;
}

interface ChargingPointProperties {
  chargingPointSn: string;
  connectorType: string; // use union if more types exist
  isDeviceOffline: boolean;
  _id: string;
  id: string;
}

/**
 * View the file based on its type
 * @param param0
 * @returns
 */
function FilePreview({ file }: { file: any }) {
  let fileTypeView;

  if (file.fileType.includes('image')) {
    fileTypeView = <img src={file.signedUrl} alt="" className="selected-image" />;
  } else if (file.fileType.includes('video')) {
    fileTypeView = (
      <video controls className="selected-video">
        <source src={file.signedUrl} type={file.fileType} />
        <track src="path/to/captions.vtt" kind="captions" label="English captions" default />
        Your browser does not support the video tag.
      </video>
    );
  }

  return <div className="single-selected-wrap">{fileTypeView}</div>;
}

/**
 * Station side panel
 * @param properties
 * @returns
 */
function StationSidePanel(properties: StationSidePanelProperties) {
  const { fetchIdFrom, id } = properties;
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();

  const [chargingStationData, setChargingStationData] = useState<AvailableStation>();
  const [chargingPointData, setChargingPointData] = useState<ChargingPointProperties[]>([]);

  const [partLength, setPartLength] = useState<number>(1);

  const amenitiesDetails = [
    {
      name: 'Gas Station',
      key: 'Gas Station',
      svgIconActive: <brandIcons.GasStationActive />,
    },
    {
      name: 'Wifi',
      key: 'Free Wifi',
      svgIconActive: <brandIcons.WifiActive />,
    },
    {
      name: 'Restroom',
      key: 'Restroom',
      svgIconActive: <brandIcons.RestroomActive />,
    },
    {
      name: 'Lounge',
      key: 'Lounge',
      svgIconActive: <brandIcons.LoungeActive />,
    },
    {
      name: 'Caffee',
      key: 'Cafe',
      svgIconActive: <brandIcons.CaffeeActive />,
    },
    {
      name: 'Shopping',
      key: 'Shopping',
      svgIconActive: <brandIcons.ShoppingActive />,
    },
    {
      name: 'Restaurant',
      key: 'Restaurant',
      svgIconActive: <brandIcons.RestaurantActive />,
    },
    {
      name: 'Relaxing Area',
      key: 'Relaxing Area',
      svgIconActive: <brandIcons.RelaxingActive />,
    },
    // {
    //   name: 'Locker',
    //   key: 'Locker',
    //   svgIconActive: <brandIcons.LockerActive />,
    // },
    // {
    //   name: 'Work Shop',
    //   key: 'Work Shop',
    //   svgIconActive: <brandIcons.WorkshopActive />,
    // },
    {
      name: 'Car Wash',
      key: 'Car Wash',
      svgIconActive: <brandIcons.CarwashActive />,
    },
  ];

  const [detailsTabs, setDetailsTabs] = useState([
    {
      label: 'Station Info',
      key: 'station-info',
      isActive: true,
    },
    {
      label: 'Device Info',
      key: 'device-info',
      isActive: false,
    },
  ]);

  const column = [
    {
      label: 'Device',
      accessorKey: 'chargingPointSn' as keyof ChargingPointProperties,
      sortAllow: true,
    },
    {
      label: 'Status',
      accessorKey: 'isDeviceOffline' as keyof ChargingPointProperties,
      sortAllow: true,
      /**
       * Function
       * @param row
       * @returns
       */
      customData: (row: ChargingPointProperties) => (
        <p>{row.isDeviceOffline ? 'Offline' : 'Online'}</p>
      ),
    },
    {
      label: 'Connector Type',
      accessorKey: 'connectorType' as keyof ChargingPointProperties,
      sortAllow: true,
    },
  ];

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchStationDetails = async (stationId: string) => {
    chargingStation
      .fetchChargingStationByStationId(stationId)
      .then((response) => {
        setChargingStationData(response?.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to fetch station details
   * @param stationId
   */
  const handleFetchDeviceDetails = async (stationId: string) => {
    chargingStation
      .fetchChargingPointByStationId(stationId)
      .then((response) => {
        setChargingPointData(response.data);
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Function to switch the tab
   * @param {string} key
   */
  const handleSwitchTab = (key: string) => {
    setDetailsTabs((previousTabs: AvailableTabProperties[]) =>
      previousTabs.map((tab) => {
        if (tab.key === key) {
          return {
            ...tab,
            isActive: true,
          };
        }
        return {
          ...tab,
          isActive: false,
        };
      }),
    );
  };

  useEffect(() => {
    const length = chargingStationData?.medias?.length || 3;
    setPartLength(Math.ceil(length / 3)); // Use Math.ceil to handle uneven division
  }, [chargingStationData?.medias]);

  useEffect(() => {
    let stationId;
    if (fetchIdFrom === 'queryParams') {
      stationId = searchParameters.get('stationId');
    } else if (fetchIdFrom === 'props') {
      stationId = id;
    }

    if (!stationId) {
      return;
    }

    handleFetchStationDetails(stationId);
    handleFetchDeviceDetails(stationId);
  }, [searchParameters, id]);

  return (
    <div className="station-side-panel-wrap">
      <div className="station-side-panel-tabs">
        {detailsTabs.map((tab) => (
          <div
            key={tab.key}
            className={`each-tab ${tab.isActive ? 'active' : ''}`}
            role="presentation"
            onClick={() => handleSwitchTab(tab.key)}>
            <h6>{tab.label}</h6>
            <div className="bottom-green" />
          </div>
        ))}
      </div>
      <div className="station-side-panel-container">
        {(() => {
          const activeTab = detailsTabs.find((detail) => detail.isActive);
          let dataToShow = null;
          if (activeTab) {
            switch (activeTab.key) {
              case 'station-info':
                dataToShow = (
                  <div className="station-data-container">
                    {chargingStationData?.medias?.length ? (
                      <div className="gallery-view-container">
                        <div className="gallery-view-column">
                          {chargingStationData?.medias
                            ?.slice(0, partLength)
                            ?.map((selectedFile) => (
                              <FilePreview
                                key={selectedFile.url} // Ensure a unique key
                                file={selectedFile}
                              />
                            ))}
                        </div>
                        <div className="gallery-view-column">
                          {chargingStationData?.medias
                            ?.slice(partLength, partLength * 2)
                            ?.map((selectedFile) => (
                              <FilePreview
                                key={selectedFile.url} // Ensure a unique key
                                file={selectedFile}
                              />
                            ))}
                        </div>
                        <div className="gallery-view-column">
                          {chargingStationData?.medias
                            ?.slice(partLength * 2)
                            ?.map((selectedFile) => (
                              <FilePreview
                                key={selectedFile.url} // Ensure a unique key
                                file={selectedFile}
                              />
                            ))}
                        </div>
                      </div>
                    ) : (
                      <div />
                    )}
                    <div className="station-content-wrap">
                      <h2>{chargingStationData?.name}</h2>
                      <h6>Address</h6>
                      <p>{chargingStationData?.address}</p>
                      {chargingStationData?.amenities?.length ? (
                        <div className="amenities-wrap">
                          <h6>Amenities</h6>
                          <div className="amenities-container">
                            {chargingStationData?.amenities?.map((eachData) => {
                              const amenityToShow =
                                amenitiesDetails?.find((amenity) => amenity.key === eachData) ||
                                amenitiesDetails?.[0];

                              return (
                                <div className="single-amenity-wrap" key={eachData}>
                                  <div className="icon-wrap">{amenityToShow?.svgIconActive}</div>
                                  <p>{amenityToShow?.name}</p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div />
                      )}
                    </div>
                  </div>
                );
                break;
              default:
                dataToShow = (
                  <div className="table-container">
                    <CustomTable
                      header={column}
                      data={chargingPointData}
                      loading={false}
                      filterAllow={false}
                      sizeChangeButtonRequired={false}
                      limit={0}
                      currentPage={0}
                      totalPages={0}
                      totalResults={0}
                      handlePageChange={() => {}}
                      handleLimitChange={() => {}}
                    />
                  </div>
                );
            }
          }
          return dataToShow;
        })()}
      </div>
    </div>
  );
}

export default StationSidePanel;
